import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    ShieldCheck,
    Smartphone,
    Copy,
    Check,
    Download,
    KeyRound,
    Loader2,
    ArrowRight,
    AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";

function formatApiErrorDetail(detail) {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail
            .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
            .filter(Boolean)
            .join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}

export default function TwoFactorSetup() {
    const navigate = useNavigate();
    const { refresh } = useAuth();
    const setupToken =
        typeof window !== "undefined" ? sessionStorage.getItem("twofa_setup_token") : null;
    const email =
        typeof window !== "undefined" ? sessionStorage.getItem("twofa_email") : null;

    const [step, setStep] = useState("loading"); // loading | scan | confirm | success
    const [secret, setSecret] = useState("");
    const [qr, setQr] = useState("");
    const [otpauth, setOtpauth] = useState("");
    const [copiedSecret, setCopiedSecret] = useState(false);

    const [code, setCode] = useState("");
    const [confirming, setConfirming] = useState(false);
    const [backupCodes, setBackupCodes] = useState([]);
    const [ack, setAck] = useState(false);

    useEffect(() => {
        if (!setupToken) {
            navigate("/login/admin");
            return;
        }
        (async () => {
            try {
                const { data } = await api.post(
                    "/auth/2fa/setup/start",
                    {},
                    { headers: { Authorization: `Bearer ${setupToken}` } }
                );
                setSecret(data.secret);
                setQr(data.qr_svg_data_url);
                setOtpauth(data.otpauth_url);
                setStep("scan");
            } catch (err) {
                toast.error(
                    formatApiErrorDetail(err?.response?.data?.detail) ||
                        "Could not start 2FA setup. Please sign in again."
                );
                navigate("/login/admin");
            }
        })();
    }, [setupToken, navigate]);

    const confirm = async (e) => {
        e.preventDefault();
        if (!/^\d{6}$/.test(code) || confirming) return;
        setConfirming(true);
        try {
            const { data } = await api.post("/auth/2fa/setup/confirm", {
                setup_token: setupToken,
                code,
            });
            setBackupCodes(data.backup_codes || []);
            localStorage.setItem("token", data.access_token);
            setStep("success");
        } catch (err) {
            toast.error(
                formatApiErrorDetail(err?.response?.data?.detail) ||
                    "Could not confirm the code."
            );
        } finally {
            setConfirming(false);
        }
    };

    const finish = async () => {
        sessionStorage.removeItem("twofa_setup_token");
        sessionStorage.removeItem("twofa_email");
        await refresh();
        navigate("/admin");
    };

    const copyText = (text, setDone) => {
        try {
            navigator.clipboard.writeText(text);
            setDone(true);
            setTimeout(() => setDone(false), 1500);
        } catch {
            toast.error("Clipboard unavailable — please copy manually.");
        }
    };

    const downloadBackup = () => {
        const content = [
            "Heritage Community College eLearning",
            "Administrator 2FA backup codes",
            `Account: ${email}`,
            `Generated: ${new Date().toISOString()}`,
            "",
            "Each code works once. Keep these somewhere safe (password manager, printed copy).",
            "",
            ...backupCodes,
        ].join("\n");
        const blob = new Blob([content], { type: "text/plain" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "heritage-admin-2fa-backup-codes.txt";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    return (
        <div
            className="min-h-[80vh] flex items-center justify-center px-4 py-12"
            data-testid="twofa-setup-page"
        >
            <Card className="w-full max-w-2xl border-border shadow-lg shadow-primary/5">
                <CardContent className="p-8">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="h-11 w-11 rounded-full bg-primary/10 text-primary flex items-center justify-center ring-1 ring-primary/15">
                            <ShieldCheck className="h-5 w-5" />
                        </div>
                        <div>
                            <h1 className="font-display text-2xl text-foreground tracking-tight leading-none">
                                Set up two-factor authentication
                            </h1>
                            <p className="text-xs text-muted-foreground mt-1">
                                Required for all administrator accounts · {email}
                            </p>
                        </div>
                    </div>

                    {step === "loading" && (
                        <div className="py-12 flex items-center justify-center text-sm text-muted-foreground gap-2">
                            <Loader2 className="h-4 w-4 animate-spin" /> Preparing your
                            authenticator setup…
                        </div>
                    )}

                    {step === "scan" && (
                        <div className="space-y-6">
                            <ol className="space-y-4 text-sm text-foreground/85">
                                <li className="flex gap-3">
                                    <span className="h-6 w-6 flex-shrink-0 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-semibold">
                                        1
                                    </span>
                                    <div>
                                        <div className="font-semibold">
                                            Install an authenticator app on your phone
                                        </div>
                                        <div className="text-xs text-muted-foreground">
                                            We recommend Google Authenticator, Authy, 1Password, or
                                            Bitwarden.
                                        </div>
                                    </div>
                                </li>

                                <li className="flex gap-3">
                                    <span className="h-6 w-6 flex-shrink-0 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-semibold">
                                        2
                                    </span>
                                    <div className="flex-1">
                                        <div className="font-semibold">
                                            Scan this QR code with the app
                                        </div>
                                        <div className="mt-3 flex gap-4 items-start">
                                            <div
                                                className="border border-border rounded-md p-3 bg-white"
                                                data-testid="twofa-qr-wrapper"
                                            >
                                                {qr ? (
                                                    <img
                                                        src={qr}
                                                        alt="QR code"
                                                        className="h-44 w-44"
                                                        data-testid="twofa-qr-image"
                                                    />
                                                ) : (
                                                    <div className="h-44 w-44 bg-secondary/50 animate-pulse rounded" />
                                                )}
                                            </div>
                                            <div className="text-xs text-muted-foreground space-y-2">
                                                <p>Can&rsquo;t scan? Enter this secret manually:</p>
                                                <div className="flex items-center gap-2">
                                                    <code className="bg-secondary/60 px-2 py-1 rounded text-[11px] font-mono break-all">
                                                        {secret}
                                                    </code>
                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            copyText(secret, setCopiedSecret)
                                                        }
                                                        className="p-1 text-muted-foreground hover:text-foreground"
                                                        aria-label="Copy secret"
                                                        data-testid="twofa-copy-secret"
                                                    >
                                                        {copiedSecret ? (
                                                            <Check className="h-3.5 w-3.5 text-primary" />
                                                        ) : (
                                                            <Copy className="h-3.5 w-3.5" />
                                                        )}
                                                    </button>
                                                </div>
                                                <p className="opacity-70">
                                                    Account name in your app: <strong>{email}</strong>
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </li>

                                <li className="flex gap-3">
                                    <span className="h-6 w-6 flex-shrink-0 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-semibold">
                                        3
                                    </span>
                                    <div className="flex-1">
                                        <div className="font-semibold">
                                            Enter the 6-digit code shown in your app
                                        </div>
                                        <form
                                            onSubmit={confirm}
                                            className="mt-3 flex gap-2 items-end"
                                        >
                                            <div className="flex-1">
                                                <Label htmlFor="code" className="sr-only">
                                                    Authenticator code
                                                </Label>
                                                <Input
                                                    id="code"
                                                    inputMode="numeric"
                                                    pattern="\d{6}"
                                                    maxLength={6}
                                                    value={code}
                                                    onChange={(e) =>
                                                        setCode(
                                                            e.target.value.replace(/\D/g, "").slice(0, 6)
                                                        )
                                                    }
                                                    placeholder="123456"
                                                    className="font-mono text-center text-lg tracking-[0.4em]"
                                                    autoFocus
                                                    data-testid="twofa-setup-code-input"
                                                />
                                            </div>
                                            <Button
                                                type="submit"
                                                disabled={!/^\d{6}$/.test(code) || confirming}
                                                className="gap-1.5"
                                                data-testid="twofa-setup-confirm-button"
                                            >
                                                {confirming ? (
                                                    <Loader2 className="h-4 w-4 animate-spin" />
                                                ) : (
                                                    <ArrowRight className="h-4 w-4" />
                                                )}
                                                Confirm
                                            </Button>
                                        </form>
                                    </div>
                                </li>
                            </ol>
                        </div>
                    )}

                    {step === "success" && (
                        <div className="space-y-6" data-testid="twofa-setup-success">
                            <div className="flex items-center gap-3 rounded-md border border-primary/20 bg-primary/5 px-4 py-3">
                                <ShieldCheck className="h-5 w-5 text-primary" />
                                <div className="text-sm">
                                    <div className="font-semibold text-foreground">
                                        Two-factor authentication is on.
                                    </div>
                                    <div className="text-muted-foreground">
                                        You&rsquo;ll be asked for a code every time you sign in.
                                    </div>
                                </div>
                            </div>

                            <div>
                                <div className="flex items-center gap-2 mb-2">
                                    <KeyRound className="h-4 w-4 text-accent" />
                                    <h3 className="font-display text-lg">Save your backup codes</h3>
                                </div>
                                <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive flex items-start gap-2 mb-3">
                                    <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                                    <span>
                                        These codes are shown <strong>once</strong>. Save them now
                                        in a password manager or print them. Each works only once,
                                        and they&rsquo;re the only way to sign in if you lose your
                                        phone.
                                    </span>
                                </div>
                                <div className="grid grid-cols-2 gap-2 mb-3">
                                    {backupCodes.map((c) => (
                                        <code
                                            key={c}
                                            className="font-mono text-sm bg-secondary/60 px-3 py-2 rounded border border-border text-center"
                                            data-testid="twofa-backup-code"
                                        >
                                            {c}
                                        </code>
                                    ))}
                                </div>
                                <div className="flex gap-2 flex-wrap">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={downloadBackup}
                                        className="gap-1.5"
                                        data-testid="twofa-backup-download"
                                    >
                                        <Download className="h-4 w-4" /> Download .txt
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() =>
                                            copyText(backupCodes.join("\n"), () => {
                                                toast.success("Backup codes copied");
                                            })
                                        }
                                        className="gap-1.5"
                                        data-testid="twofa-backup-copy"
                                    >
                                        <Copy className="h-4 w-4" /> Copy all
                                    </Button>
                                </div>
                            </div>

                            <label className="flex items-start gap-2 text-sm cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={ack}
                                    onChange={(e) => setAck(e.target.checked)}
                                    className="mt-1"
                                    data-testid="twofa-backup-ack"
                                />
                                <span>
                                    I&rsquo;ve saved these backup codes somewhere safe. I understand
                                    they won&rsquo;t be shown again.
                                </span>
                            </label>

                            <Button
                                onClick={finish}
                                disabled={!ack}
                                className="w-full gap-1.5"
                                data-testid="twofa-setup-finish"
                            >
                                <Smartphone className="h-4 w-4" /> Continue to the admin dashboard
                            </Button>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
